// convert to hook

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { setLicenceValidity } from "../reducers/licence";
import { store } from "../ProviderApp";
import _ from "lodash";

// Standard API response wrapper type
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

// Typed wrapper functions - defaults to unknown to enforce type safety
// Specify response types at call sites: axiosWrapper.post<MyResponse>(...)
export const typedAxios = {
  post<TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.post<TResponse>(url, data, config);
  },

  get<TResponse = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<TResponse>> {
    return axios.get<TResponse>(url, config);
  },

  put<TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.put<TResponse>(url, data, config);
  },

  delete<TResponse = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<TResponse>> {
    return axios.delete<TResponse>(url, config);
  },

  patch<TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.patch<TResponse>(url, data, config);
  },
};

axios.interceptors.request.use(
  (config) => {
    const returnedConfig = {
      ...config,
      onUploadProgress: (progressEvent: any) => {
        if (config.onUploadProgress) {
          (config.onUploadProgress as any)(
            Math.floor((progressEvent.loaded * 100) / progressEvent.total),
            progressEvent.loaded,
          );
        }
      },
    };
    returnedConfig.withCredentials = true;
    return returnedConfig;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      _.get(error, "response.status") == "401" &&
      !["/googleLoginCallback", "/95789", "/signup", "/thank-you", "/pdfExport", "/"].includes(window.location.pathname)
    ) {
      localStorage.removeItem("isLogedIn");
      const errorMessage = error.response.data.errorMessage;

      const searchParams = new URLSearchParams();
      searchParams.set("redirectUrl", encodeURIComponent(window.location.pathname + window.location.search));
      if (errorMessage) {
        searchParams.set("errorMessage", encodeURIComponent(errorMessage));
      }

      const redirectUrl = `/?${searchParams.toString()}${window.location.hash}`;
      window.location.href = redirectUrl;
    } else if (_.get(error, "response.status") == "403" && _.get(error, "response.data.errorCode") === 1001) {
      console.log("licence expired");
      store.dispatch(setLicenceValidity(false));
    } else if (_.get(error, "response.status") == "429") {
      return false;
    }
    return Promise.reject(error);
  },
);

// Default export uses typed wrapper - call sites must specify response type or narrow the result
export default typedAxios;

// Named exports for flexibility
export const axiosWrapper = typedAxios;
